import { usePageContext } from "vike-react/usePageContext";
import { useEffect, useState } from "react";
import {
  overallSummary, getVocabularyStats,
  computeVocabOverallProgress, computeVocabStateCountsByCEFR,
  computeGrammarProgressRecentCEFR, computeRecentAccuracyByTense, readProgress,
  computeSkills, type Skill, computeCEFR, type CEFRGrade,
} from "../../utils/progress.js";
import { MotifBand, motifLabel } from "../../components/Motifs.js";
import { langCodeToName } from "../../utils/appTheme.js";

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
type CEFRLevel = typeof CEFR_LEVELS[number];

const CEFR_META: Record<CEFRLevel, { description: string; min: number }> = {
  A1: { min: 0,   description: 'Basic phrases & present tense' },
  A2: { min: 50,  description: 'Everyday topics, past tense' },
  B1: { min: 120, description: 'Familiar topics, 3+ tenses' },
  B2: { min: 250, description: 'Complex topics, subjunctive' },
  C1: { min: 400, description: 'Advanced fluency, all tenses' },
  C2: { min: 600, description: 'Near-native mastery' },
};

function CEFRTrack({ current }: { current: CEFRLevel }) {
  const idx = CEFR_LEVELS.indexOf(current);
  return (
    <div style={{ display: 'flex', alignItems: 'center', paddingTop: 4 }}>
      {CEFR_LEVELS.map((lvl, i) => {
        const isActive = i === idx;
        const isPast = i < idx;
        const isLast = i === CEFR_LEVELS.length - 1;
        return (
          <div key={lvl} style={{ display: 'flex', alignItems: 'center', flex: isLast ? 0 : 1 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {/* Node */}
              <div style={{
                width: isActive ? 30 : 20, height: isActive ? 30 : 20, borderRadius: '50%',
                background: isActive ? 'var(--accent)' : isPast ? C.accentBg : C.surf2,
                border: `2px solid ${isActive ? 'var(--accent)' : isPast ? 'var(--accent-border)' : C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s',
              }}>
                {isActive
                  ? <span style={{ fontFamily: AF.m, fontSize: 9, fontWeight: 700, color: C.bg }}>{lvl}</span>
                  : isPast
                  ? <span style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--accent)' }}>✓</span>
                  : <span style={{ fontFamily: AF.m, fontSize: 8, color: C.dim }}>{lvl}</span>}
              </div>
              {/* Label */}
              <div style={{
                position: 'absolute', top: isActive ? 36 : 26, left: '50%', transform: 'translateX(-50%)',
                fontFamily: AF.m, fontSize: isActive ? 9 : 8, whiteSpace: 'nowrap',
                color: isActive ? 'var(--accent)' : isPast ? C.muted : C.dim,
                fontWeight: isActive ? 700 : 400,
              }}>{lvl}</div>
            </div>
            {!isLast && (
              <div style={{
                flex: 1, height: 2, marginBottom: isActive ? -2 : 0,
                background: isPast ? 'var(--accent-border)' : C.surf2,
              }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: C.border, margin: '32px 0' }}/>;
}

const skillColor = (score: number) => score >= 70 ? 'var(--success)' : score >= 40 ? 'var(--accent)' : score > 0 ? 'var(--warn)' : C.dim;

function SkillsRadar({ skills, size }: { skills: Skill[]; size: number }) {
  const cx = size / 2, cy = size / 2, R = size / 2 - 38, n = skills.length;
  const pt = (i: number, r: number): [number, number] => {
    const a = (-90 + i * (360 / n)) * Math.PI / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const rings = [0.25, 0.5, 0.75, 1];
  const valuePts = skills.map((s, i) => pt(i, R * Math.max(0.02, s.score / 100)));
  const PAD = 52;
  return (
    <svg viewBox={`${-PAD} 0 ${size + 2 * PAD} ${size}`} width={size + 2 * PAD} height={size} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} role="img" aria-label="Skills radar">
      {rings.map((rr, ri) => (
        <polygon key={ri} points={skills.map((_, i) => pt(i, R * rr).join(',')).join(' ')} fill="none" stroke="var(--border)" strokeWidth="1" opacity={ri === rings.length - 1 ? 0.8 : 0.35}/>
      ))}
      {skills.map((_, i) => { const [x, y] = pt(i, R); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border)" strokeWidth="1" opacity="0.35"/>; })}
      <polygon points={valuePts.map(p => p.join(',')).join(' ')} fill="var(--accent)" fillOpacity="0.16" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round"/>
      {valuePts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="var(--accent)"/>)}
      {skills.map((s, i) => {
        const [lx, ly] = pt(i, R + 22);
        const anchor = Math.abs(lx - cx) < 8 ? 'middle' : lx > cx ? 'start' : 'end';
        return (
          <text key={i} x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" fill="var(--muted)" style={{ fontFamily: AF.m, fontSize: 9 }}>
            {s.icon} {s.name.split(' ')[0]}
          </text>
        );
      })}
    </svg>
  );
}

const SKILL_PATHS: Record<string, string> = {
  vocabulary: '/vocabulary',
  grammar: '/practice',
  reading: '/comprehension',
  writing: '/sentences',
  speaking: '/speaking',
};
const LANG_SLUG: Record<string, string> = { es: 'spanish', pt: 'portuguese', fi: 'finnish' };

function SkillCard({ s, langCode }: { s: Skill; langCode: string }) {
  const col = skillColor(s.score);
  const tipHref = s.score === 0 && s.competence && SKILL_PATHS[s.id]
    ? `/learn-${LANG_SLUG[langCode] ?? 'spanish'}${SKILL_PATHS[s.id]}`
    : null;
  return (
    <div style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
        <span style={{ fontSize: 18 }}>{s.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 500, color: C.text }}>{s.name}</div>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: col, letterSpacing: '0.04em' }}>{s.tier}</div>
        </div>
        <span style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: col, letterSpacing: '-0.5px' }}>{s.score}</span>
      </div>
      <div style={{ height: 5, background: C.surf2, borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ width: `${s.score}%`, height: '100%', background: col, borderRadius: 3, transition: 'width 0.7s' }}/>
      </div>
      <div style={{ fontFamily: AF.b, fontSize: 11.5, color: C.muted, lineHeight: 1.4 }}>{s.blurb}</div>
      {tipHref && (
        <a href={tipHref} style={{ display: 'block', fontFamily: AF.b, fontSize: 11, color: 'var(--accent)', marginTop: 6, textDecoration: 'none' }}>→ {s.tip}</a>
      )}
    </div>
  );
}

export default function Page() {
  const pageContext = usePageContext();
  const langCode = pageContext.routeParams?.language ?? 'es';
  const language = langCodeToName(langCode);

  const [isMobile, setIsMobile] = useState(false);
  const [summary, setSummary] = useState({ attempts: 0, accuracy: 0 });
  const [vocabStats, setVocabStats] = useState({ wellKnownWords: 0, studiedWords: 0 });
  const [vocabLevels, setVocabLevels] = useState<Array<{ level: string; totalWords: number; learnedWords: number }>>([]);
  const [deckCounts, setDeckCounts] = useState<Array<{ level: string; counts: { unseen: number; evaluating: number; to_learn: number; known: number } }>>([]);
  const [cefr, setCefr] = useState<CEFRGrade | null>(null);
  const [tenseAccuracy, setTenseAccuracy] = useState<Array<{ tense: string; recentAccuracy: number }>>([]);
  const [masteredVerbs, setMasteredVerbs] = useState<string[]>([]);
  const [showAllVerbs, setShowAllVerbs] = useState(false);
  const [skills, setSkills] = useState<Skill[]>([]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    setSummary(overallSummary());
    setVocabStats(getVocabularyStats());
    setTenseAccuracy(computeRecentAccuracyByTense(10));
    setCefr(computeCEFR(langCode));
    const store = readProgress();
    const verbs = Object.entries(store.byVerb)
      .filter(([, s]) => s.attempts >= 5 && (s.correct / s.attempts) >= 0.8)
      .map(([v]) => v).sort();
    setMasteredVerbs(verbs);
    setSkills(computeSkills(langCode));
  }, [langCode]);

  useEffect(() => {
    async function load() {
      try {
        const [vp, dc] = await Promise.all([
          computeVocabOverallProgress(langCode),
          computeVocabStateCountsByCEFR(langCode),
        ]);
        setVocabLevels(vp.levels);
        setDeckCounts(dc);
      } catch {}
    }
    load();
  }, [langCode]);

  const hp = isMobile ? '16px' : '48px';

  const TENSE_ORDER = ['present','preterite','imperfect','future','conditional','present_subjunctive','imperfect_subjunctive','present_perfect','pluperfect'] as const;
  const TENSE_LABELS: Record<string, string> = {
    present: 'Present', preterite: 'Preterite', imperfect: 'Imperfect',
    future: 'Future', conditional: 'Conditional', present_subjunctive: 'Pres. Subjunctive',
    imperfect_subjunctive: 'Imp. Subjunctive', present_perfect: 'Pres. Perfect', pluperfect: 'Pluperfect',
  };
  const TENSE_CEFR: Record<string, string> = {
    present: 'A1', preterite: 'A2', imperfect: 'A2', future: 'B1',
    conditional: 'B1', present_subjunctive: 'B2',
    imperfect_subjunctive: 'C1', present_perfect: 'B1', pluperfect: 'C1',
  };
  const tenseMap = Object.fromEntries(tenseAccuracy.map(t => [t.tense, t.recentAccuracy]));
  const tenses = TENSE_ORDER.map(id => ({ id, name: TENSE_LABELS[id], pct: tenseMap[id] ?? 0, cefr: TENSE_CEFR[id] ?? 'A1' }));

  const cefrLabel: CEFRLevel = (cefr?.label ?? 'A1') as CEFRLevel;
  const nextLevel = CEFR_LEVELS[CEFR_LEVELS.indexOf(cefrLabel) + 1];
  const displayedVerbs = showAllVerbs ? masteredVerbs : masteredVerbs.slice(0, 24);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>

      {/* Motif header */}
      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${C.border}` }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.55,
          WebkitMaskImage: 'linear-gradient(to left, #000, transparent 60%)',
          maskImage: 'linear-gradient(to left, #000, transparent 60%)',
        }}>
          <MotifBand language={language} height={isMobile ? 108 : 132} opacity={0.7}/>
        </div>
        <div style={{ position: 'relative', padding: `26px ${hp} 20px` }}>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 9 }}>
            {motifLabel(language)}
          </div>
          <h1 style={{ fontFamily: AF.h, fontSize: isMobile ? 24 : 30, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.8px' }}>
            Your Progress
          </h1>
        </div>
      </div>

      <div style={{ padding: `28px ${hp}` }}>

        {/* ── CEFR Level card ── */}
        <div style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12, padding: isMobile ? '20px 18px 28px' : '24px 28px 30px', marginBottom: 28 }}>
          {/* Top: level + score breakdown */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <div>
              <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
                Estimated level
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontFamily: AF.h, fontSize: 52, fontWeight: 700, color: 'var(--accent)', lineHeight: 1, letterSpacing: '-3px' }}>
                  {cefrLabel}
                </span>
                {nextLevel && (
                  <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>
                    → {nextLevel}
                  </span>
                )}
              </div>
              <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginTop: 4 }}>
                {CEFR_META[cefrLabel].description}
              </div>
            </div>

            <div style={{ display: 'flex', gap: isMobile ? 16 : 24, flexWrap: 'wrap' }}>
              {[
                { value: vocabStats.wellKnownWords, label: 'vocab known' },
                { value: masteredVerbs.length,      label: 'verbs mastered' },
                { value: cefr?.overall ?? 0,         label: 'overall score' },
              ].map(({ value, label }) => (
                <div key={label} style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', lineHeight: 1 }}>{value}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted, marginTop: 3 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Progress bar to next level */}
          {nextLevel && (
            <div style={{ marginBottom: cefr?.cappedBy ? 16 : 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.08em' }}>
                  {cefrLabel} → {nextLevel}
                </span>
                <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)' }}>{cefr?.progressToNext ?? 0}%</span>
              </div>
              <div style={{ height: 5, background: C.surf2, borderRadius: 3 }}>
                <div style={{ width: `${cefr?.progressToNext ?? 0}%`, height: '100%', borderRadius: 3, background: 'var(--accent)', transition: 'width 0.8s' }}/>
              </div>
            </div>
          )}

          {/* Gating note — what's holding the level back */}
          {cefr?.cappedBy && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 24,
              padding: '10px 14px', borderRadius: 8,
              background: 'var(--warn-bg)', border: '1px solid var(--warn-border)',
            }}>
              <span style={{ fontSize: 13, lineHeight: 1.3 }}>🔒</span>
              <span style={{ fontFamily: AF.b, fontSize: 12.5, color: 'var(--text)', lineHeight: 1.45 }}>
                <strong style={{ color: 'var(--warn)' }}>Level capped.</strong> {cefr.cappedBy}
                {cefr.weakest && cefr.weakest.score < 100 && (
                  <> Your weakest skill right now is <strong>{cefr.weakest.name}</strong> ({cefr.weakest.score}/100).</>
                )}
              </span>
            </div>
          )}

          {/* CEFR track */}
          <CEFRTrack current={cefrLabel}/>
          <div style={{ height: 24 }}/>
        </div>

        {/* ── Skills ── */}
        {skills.length > 0 && (() => {
          const competences = skills.filter(s => s.competence);
          const consistency = skills.find(s => s.id === 'consistency');
          return (
            <>
              <SectionLabel>Skills — the parts of your language</SectionLabel>
              <div style={{ display: 'flex', gap: isMobile ? 16 : 28, alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', marginBottom: 20 }}>
                <div style={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'flex-start', marginTop: isMobile ? 0 : 4 }}>
                  <SkillsRadar skills={competences} size={isMobile ? 260 : 240}/>
                </div>
                <div style={{ flex: 1, width: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                  {competences.map(s => <SkillCard key={s.id} s={s} langCode={langCode}/>)}
                </div>
              </div>

              {consistency && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, background: C.surf, border: `1px solid ${C.border}`, marginBottom: 32 }}>
                  <span style={{ fontSize: 20 }}>{consistency.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <span style={{ fontFamily: AF.b, fontSize: 13, fontWeight: 500, color: C.text }}>{consistency.name}</span>
                      <span style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: skillColor(consistency.score), letterSpacing: '-0.3px' }}>{consistency.score}/100</span>
                    </div>
                    <div style={{ height: 4, background: C.surf2, borderRadius: 2, overflow: 'hidden', marginBottom: 5 }}>
                      <div style={{ width: `${consistency.score}%`, height: '100%', background: skillColor(consistency.score), borderRadius: 2, transition: 'width 0.7s' }}/>
                    </div>
                    <div style={{ fontFamily: AF.b, fontSize: 11.5, color: C.muted }}>{consistency.blurb}</div>
                  </div>
                </div>
              )}

              <Divider/>
            </>
          );
        })()}

        {/* ── Stat cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 32 }}>
          {[
            { value: masteredVerbs.length, label: 'Verbs mastered',   sub: 'of 2,400+',  color: C.text },
            { value: `${summary.accuracy}%`, label: 'Accuracy',       sub: 'All time',   color: 'var(--success)' },
            { value: summary.attempts,      label: 'Total attempts',  sub: 'All time',   color: C.muted },
            { value: vocabStats.wellKnownWords, label: 'Words known', sub: 'Vocabulary', color: 'var(--accent)' },
          ].map(({ value, label, sub, color }) => (
            <div key={label} style={{
              background: C.surf, border: `1px solid ${C.border}`, borderRadius: 10,
              padding: isMobile ? '14px 16px' : '16px 20px',
            }}>
              <div style={{ fontFamily: AF.h, fontSize: isMobile ? 26 : 30, fontWeight: 700, color, letterSpacing: '-1px', lineHeight: 1 }}>{value}</div>
              <div style={{ fontFamily: AF.b, fontSize: 13, color: C.text, fontWeight: 500, marginTop: 6 }}>{label}</div>
              <div style={{ fontFamily: AF.b, fontSize: 11, color: C.dim, marginTop: 2 }}>{sub}</div>
            </div>
          ))}
        </div>

        <Divider/>

        {/* ── Vocabulary deck ── */}
        {vocabLevels.length > 0 && (
          <>
            <SectionLabel>Vocabulary deck</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {vocabLevels.map(({ level, totalWords, learnedWords }) => {
                const dc = deckCounts.find(d => d.level === level)?.counts ?? { unseen: totalWords, evaluating: 0, to_learn: 0, known: 0 };
                const reviewing = dc.evaluating + dc.to_learn;
                const knownPct  = totalWords > 0 ? (dc.known    / totalWords) * 100 : 0;
                const reviewPct = totalWords > 0 ? (reviewing   / totalWords) * 100 : 0;

                return (
                  <div key={level} style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 9, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontFamily: AF.m, fontSize: 11, fontWeight: 700, color: 'var(--accent)', minWidth: 22 }}>{level}</span>
                        <span style={{ fontFamily: AF.b, fontSize: 13, color: dc.known > 0 ? C.text : C.dim }}>{totalWords} words</span>
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {dc.known    > 0 && <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--success)' }}>✓ {dc.known}</span>}
                        {reviewing   > 0 && <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--warn)' }}>↺ {reviewing}</span>}
                        {dc.known === 0 && reviewing === 0
                          ? <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim }}>Not started</span>
                          : dc.known >= totalWords
                          ? <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--success)' }}>Complete ✓</span>
                          : <span style={{ fontFamily: AF.m, fontSize: 10, color: C.muted }}>{Math.round(knownPct)}%</span>
                        }
                      </div>
                    </div>
                    <div style={{ height: 4, background: C.surf2, borderRadius: 2, overflow: 'hidden', display: 'flex' }}>
                      {dc.known   > 0 && <div style={{ width: `${knownPct}%`,  background: 'var(--success)', transition: 'width 0.6s' }}/>}
                      {reviewing  > 0 && <div style={{ width: `${reviewPct}%`, background: 'var(--warn)',    transition: 'width 0.6s' }}/>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 18, marginBottom: 32 }}>
              {[['var(--success)', 'Known'], ['var(--warn)', 'Reviewing'], [C.surf2, 'Unseen']].map(([color, label]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 9, height: 9, borderRadius: 2, background: color, border: color === C.surf2 ? `1px solid ${C.border}` : 'none' }}/>
                  <span style={{ fontFamily: AF.b, fontSize: 11, color: C.muted }}>{label}</span>
                </div>
              ))}
            </div>

            <Divider/>
          </>
        )}

        {/* ── Tense mastery ── */}
        <SectionLabel>Tense mastery</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 32 }}>
          {tenses.map(({ id, name, pct, cefr }) => (
            <div key={id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '11px 16px', borderRadius: 8,
              background: pct > 0 ? C.surf : 'transparent',
              border: `1px solid ${pct > 0 ? C.border : 'transparent'}`,
              marginBottom: 2,
            }}>
              <span style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--accent)', width: 20, flexShrink: 0, letterSpacing: '0.05em' }}>{cefr}</span>
              <span style={{ fontFamily: AF.b, fontSize: 14, color: pct > 0 ? C.text : C.dim, flex: 1, fontWeight: pct > 0 ? 500 : 400 }}>{name}</span>
              <div style={{ width: isMobile ? 80 : 120, height: 4, background: C.surf2, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`, height: '100%', borderRadius: 2, transition: 'width 0.6s',
                  background: pct > 60 ? 'var(--success)' : pct > 30 ? 'var(--accent)' : pct > 0 ? 'var(--error)' : 'transparent',
                }}/>
              </div>
              <span style={{
                fontFamily: AF.m, fontSize: 11, width: 32, textAlign: 'right', flexShrink: 0,
                color: pct > 60 ? 'var(--success)' : pct > 0 ? 'var(--accent)' : C.dim,
              }}>
                {pct > 0 ? `${pct}%` : '—'}
              </span>
            </div>
          ))}
        </div>

        <Divider/>

        {/* ── Mastered verbs ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionLabel>Mastered verbs</SectionLabel>
          {masteredVerbs.length > 24 && (
            <span onClick={() => setShowAllVerbs(v => !v)} style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', cursor: 'pointer', marginTop: -16 }}>
              {showAllVerbs ? 'Show less ↑' : `See all ${masteredVerbs.length} →`}
            </span>
          )}
        </div>

        {masteredVerbs.length === 0 ? (
          <div style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 10, padding: '24px 20px', textAlign: 'center' }}>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 4 }}>No verbs mastered yet</div>
            <div style={{ fontFamily: AF.b, fontSize: 12, color: C.dim }}>
              Complete 5+ drills on a verb with ≥80% accuracy to master it.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32 }}>
            {displayedVerbs.map(v => (
              <span key={v} style={{
                fontFamily: AF.m, fontSize: 13, color: C.text,
                background: C.surf, border: `1px solid ${C.border}`,
                padding: '5px 12px', borderRadius: 6,
              }}>{v}</span>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
